// src/views/roles/MyTenantRoles.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Paper,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import Markdown from "../../components/Markdown.jsx";
import StatusChip from "../../components/StatusChip";

import { tokens } from "../../theme/tokens";
import { graphqlEndpoint } from "../../config";
import { useResolvedTenantId } from "../../hooks/useResolvedTenantId";

import {
  READ_ASSIGNED_TENANT_ROLES_FOR_TENANT_USER,
  READ_TENANT_USERS_BY_TENANT,
} from "../../graph/tenantUserRoleAssignments/queries";

const client = new GraphQLClient(graphqlEndpoint);

const markdownStyles = {
  color: "text.secondary",

  "& > :first-of-type": {
    mt: 0,
  },

  "& > :last-child": {
    mb: 0,
  },

  "& h1, & h2, & h3, & h4": {
    color: "text.primary",
    fontSize: "1rem",
    fontWeight: 700,
    lineHeight: 1.4,
    mt: 2,
    mb: 0.75,
  },

  "& p": {
    fontSize: "0.875rem",
    lineHeight: 1.7,
    mt: 0,
    mb: 1,
  },

  "& ul, & ol": {
    fontSize: "0.875rem",
    lineHeight: 1.7,
    mt: 0.5,
    mb: 1,
    pl: 3,
  },

  "& li": {
    mb: 0.4,
  },

  "& blockquote": {
    borderLeft: "3px solid",
    borderColor: "primary.main",
    pl: 1.5,
    ml: 0,
    fontStyle: "italic",
  },

  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.85rem",
  },

  "& th, & td": {
    border: "1px solid",
    borderColor: "divider",
    px: 1,
    py: 0.75,
    textAlign: "left",
  },

  "& hr": {
    border: 0,
    borderTop: "1px solid",
    borderColor: "divider",
    my: 2,
  },
};

export default function MyTenantRoles() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const {
    user,
    tenantId,
    tenantName,
  } = useResolvedTenantId();

  const [tenantUsers, setTenantUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  const tenantUserId = useMemo(() => {
    if (!user?.id || !tenantUsers.length) {
      return null;
    }

    return (
      tenantUsers.find(
        (tenantUser) => tenantUser.userId === user.id
      )?.id || null
    );
  }, [user?.id, tenantUsers]);

  useEffect(() => {
    const loadTenantUsers = async () => {
      if (!tenantId) {
        setTenantUsers([]);
        return;
      }

      try {
        const {
          readTenantUsersByTenant,
        } = await client.request(
          READ_TENANT_USERS_BY_TENANT,
          { tenantId }
        );

        setTenantUsers(
          readTenantUsersByTenant || []
        );
      } catch (err) {
        console.error(
          "Failed to load tenant users",
          err
        );

        setTenantUsers([]);
      }
    };

    loadTenantUsers();
  }, [tenantId]);

  useEffect(() => {
    const loadRoles = async () => {
      if (!tenantUserId) {
        setRoles([]);
        return;
      }

      try {
        const {
          readAssignedTenantRolesForTenantUser,
        } = await client.request(
          READ_ASSIGNED_TENANT_ROLES_FOR_TENANT_USER,
          { id: tenantUserId }
        );

        setRoles(
          readAssignedTenantRolesForTenantUser || []
        );
      } catch (err) {
        console.error(
          "Failed to load assigned tenant roles",
          err
        );

        setRoles([]);
      }
    };

    loadRoles();
  }, [tenantUserId]);

  return (
    <Box>
      <Header
        title="MY ROLES"
        subtitle={
          tenantName
            ? `Your responsibilities at ${tenantName}`
            : "Your assigned responsibilities"
        }
      />

      {roles.length === 0 ? (
        <Box mt={3}>
          <Typography variant="h6">
            No roles assigned yet
          </Typography>

          <Typography
            color="text.secondary"
            mt={1}
          >
            Roles will appear here once an admin assigns
            you to them.
          </Typography>
        </Box>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))"
          alignItems="start"
          mt={2}
        >
          {roles.map((role) => (
            <Paper
              key={role.id}
              sx={{
                p: 2,
                border: `1px solid ${colors.scalpelTeal[400]}`,
                bgcolor: "background.paper",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={1}
                mb={2}
              >
                <Typography
                  variant="h4"
                  sx={{
                    lineHeight: 1.35,
                  }}
                >
                  {role.name}
                </Typography>

                <Box flexShrink={0}>
                  <StatusChip
                    label="Assigned"
                    tone="success"
                  />
                </Box>
              </Box>

              <RoleMarkdownSection
                title="Description"
                value={role.description}
                fallback="No description provided yet."
              />

              <RoleMarkdownSection
                title="Mission"
                value={role.mission}
                fallback="No mission statement provided yet."
              />
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

const RoleMarkdownSection = ({
  title,
  value,
  fallback,
}) => (
  <Box
    component="section"
    sx={{
      mb: 2,

      "&:last-of-type": {
        mb: 0,
      },
    }}
  >
    <Typography
      variant="subtitle2"
      color="text.secondary"
      sx={{ mb: 0.75 }}
    >
      {title}
    </Typography>

    {value?.trim() ? (
      <Box sx={markdownStyles}>
        <Markdown>{value}</Markdown>
      </Box>
    ) : (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        {fallback}
      </Typography>
    )}
  </Box>
);